import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Grid,
    DialogActions,
    DialogContent,
    TextField,
    CircularProgress,
    Button,
    MenuItem,
    Box,
    InputLabel,
    NativeSelect,
    FormControl,
    FormLabel,
    Radio,
    FormControlLabel,
    RadioGroup
} from "@mui/material";
import { useSnackbar } from "notistack";
import http, { ROUTES } from 'src/utils/httpHelper';
import InvoiceClientConfig from "src/models/invoiceClientConfig";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import GeneralAutocomplete from "src/components/GeneralAutocomplete";

interface InvoiceClientConfigFormProps {
    modalCloseCallback,
    onSuccessCallback
    item?: InvoiceClientConfig
}

const InvoiceClientConfigForm = ({ modalCloseCallback, onSuccessCallback, item }: InvoiceClientConfigFormProps) => {
    const { t }: { t: any } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const { handleSubmit, formState: { errors, isSubmitting, isValid }, control, setValue, trigger, reset } = useForm({
        mode: 'onTouched',
        resolver: yupResolver(InvoiceClientConfig.getRules(item)),
        defaultValues: InvoiceClientConfig.getInitValues(item)
    });

    const [clientOptions, setClientOptions] = useState([]);
    const [isFormDataLoadingClient, setIsFormDataLoadingClient] = useState(false);
    useEffect(() =>{
        setIsFormDataLoadingClient(true);
        http.get(ROUTES.BASE + '/api/select-options/clients')
            .then(response => {
                setClientOptions(response.data);
            })
            .catch(error => console.error(error))
            .finally(()=>setIsFormDataLoadingClient(false));
    }, []);

    const [expeditorOptions, setExpeditorOptions] = useState([]);
    const [isFormDataLoadingExpeditor, setIsFormDataLoadingExpeditor] = useState(false);
    useEffect(() =>{
        setIsFormDataLoadingExpeditor(true);
        http.get(ROUTES.BASE + '/api/select-options/invoice-expeditors')
            .then(response => {
                setExpeditorOptions(response.data);
            })
            .catch(error => console.error(error))
            .finally(()=>setIsFormDataLoadingExpeditor(false));
    }, []);

    return (
        <form noValidate onSubmit={handleSubmit(async (values) => {
            await http.request({
                method: item ? 'put' : 'post',
                url: item ? `/api/invoice-client-configs/${item.id}` : '/api/invoice-client-configs',
                data: values
            })
            .then((response) => {
                onSuccessCallback();
                enqueueSnackbar(response.data.message, { variant: 'success' });
            })
            .catch((error) => {
                console.error(error);
                enqueueSnackbar(error.message, { variant: 'error' });
            });
        })}>
            <DialogContent
                dividers
                sx={{
                    p: 3,
                }}
            >
                <Grid container spacing={3}>
                    <Grid item xs={12} md={12}>
                        <GeneralAutocomplete
                            control={control}
                            formField="client_id"
                            label={t("FIELDS.CLIENT")}
                            isFormDataLoading={isFormDataLoadingClient}
                            options={clientOptions}
                            setValue={setValue}
                            trigger={trigger}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <GeneralAutocomplete
                            control={control}
                            formField="invoice_expeditor_id"
                            label={t("FIELDS.EXPEDITOR")}
                            isFormDataLoading={isFormDataLoadingExpeditor}
                            options={expeditorOptions}
                            setValue={setValue}
                            trigger={trigger}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="note"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.note)}
                                    fullWidth
                                    label={t("FIELDS.NOTE")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="font"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.font)}
                                    fullWidth
                                    label={t("FIELDS.FONT")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="font_size"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.font_size)}
                                    fullWidth
                                    label={t("FIELDS.FONT_SIZE")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="font_size_title"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.font_size_title)}
                                    fullWidth
                                    label={t("FIELDS.FONT_SIZE_TITLE")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={12} mt={2}>
                        <InputLabel variant="standard" htmlFor="select-combine">
                            {t("FIELDS.COMBINE_ITEMS")}
                        </InputLabel>
                        <NativeSelect
                            defaultValue={item?item.combine:0}
                            fullWidth
                            inputProps={{
                                name: 'combine',
                                id: 'select-combine',
                            }}
                            onChange={(event) => {
                                setValue('combine', event.target.value);
                            }}
                            >
                                <option value={0}>No combinar</option>
                                <option value={1}>Combinar por clave del SAT (ClaveProdServ)</option>
                                <option value={2}>Combinar por clave del SAP (NoIdentificacion)</option>
                        </NativeSelect>
                    </Grid>
                </Grid>
                <Grid container spacing={1} 
                    sx={{
                        display: 'flex', 
                        justifyContent: 'space-between',
                        pt: 3,
                        pl: 1
                    }}
                >
                    <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">{t("FIELDS.CONF_CFDI_USES")}</FormLabel>
                            <Controller
                                rules={{ required: true }}
                                control={control}
                                name="conf_cfdi_uses"
                                render={({ field }) => (
                                    <RadioGroup {...field}>
                                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                            <FormControlLabel
                                                value="0"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_0")}
                                            />
                                            <FormControlLabel
                                                value="1"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_1")}
                                            />
                                            <FormControlLabel
                                                value="2"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_2")}
                                            />
                                        </Box>
                                    </RadioGroup>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">{t("FIELDS.CONF_OBJ_IMP")}</FormLabel>
                            <Controller
                                rules={{ required: true }}
                                control={control}
                                name="conf_obj_imp"
                                render={({ field }) => (
                                    <RadioGroup {...field}>
                                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                            <FormControlLabel
                                                value="0"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_0")}
                                            />
                                            <FormControlLabel
                                                value="1"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_1")}
                                            />
                                            <FormControlLabel
                                                value="2"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_2")}
                                            />
                                        </Box>
                                    </RadioGroup>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">{t("FIELDS.CONF_PAYMENT_METHODS")}</FormLabel>
                            <Controller
                                rules={{ required: true }}
                                control={control}
                                name="conf_payment_methods"
                                render={({ field }) => (
                                    <RadioGroup {...field}>
                                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                            <FormControlLabel
                                                value="0"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_0")}
                                            />
                                            <FormControlLabel
                                                value="1"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_1")}
                                            />
                                            <FormControlLabel
                                                value="2"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_2")}
                                            />
                                        </Box>
                                    </RadioGroup>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">{t("FIELDS.CONF_PAYMENT_TYPES")}</FormLabel>
                            <Controller
                                rules={{ required: true }}
                                control={control}
                                name="conf_payment_types"
                                render={({ field }) => (
                                    <RadioGroup {...field}>
                                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                            <FormControlLabel
                                                value="0"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_0")}
                                            />
                                            <FormControlLabel
                                                value="1"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_1")}
                                            />
                                            <FormControlLabel
                                                value="2"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_2")}
                                            />
                                        </Box>
                                    </RadioGroup>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">{t("FIELDS.CONF_PROD_SERV_KEYS")}</FormLabel>
                            <Controller
                                rules={{ required: true }}
                                control={control}
                                name="conf_prod_serv_keys"
                                render={({ field }) => (
                                    <RadioGroup {...field}>
                                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                            <FormControlLabel
                                                value="0"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_0")}
                                            />
                                            <FormControlLabel
                                                value="1"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_1")}
                                            />
                                            <FormControlLabel
                                                value="2"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_2")}
                                            />
                                        </Box>
                                    </RadioGroup>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">{t("FIELDS.CONF_TAX_REGIMES_EXPEDITOR")}</FormLabel>
                            <Controller
                                rules={{ required: true }}
                                control={control}
                                name="conf_tax_regimes_expeditor"
                                render={({ field }) => (
                                    <RadioGroup {...field}>
                                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                            <FormControlLabel
                                                value="0"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_0")}
                                            />
                                            <FormControlLabel
                                                value="1"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_1")}
                                            />
                                            <FormControlLabel
                                                value="2"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_2")}
                                            />
                                        </Box>
                                    </RadioGroup>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">{t("FIELDS.CONF_TAX_REGIMES_CLIENT")}</FormLabel>
                            <Controller
                                rules={{ required: true }}
                                control={control}
                                name="conf_tax_regimes_client"
                                render={({ field }) => (
                                    <RadioGroup {...field}>
                                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                            <FormControlLabel
                                                value="0"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_0")}
                                            />
                                            <FormControlLabel
                                                value="1"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_1")}
                                            />
                                            <FormControlLabel
                                                value="2"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_2")}
                                            />
                                        </Box>
                                    </RadioGroup>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">{t("FIELDS.CONF_TAX_TYPES")}</FormLabel>
                            <Controller
                                rules={{ required: true }}
                                control={control}
                                name="conf_tax_types"
                                render={({ field }) => (
                                    <RadioGroup {...field}>
                                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                            <FormControlLabel
                                                value="0"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_0")}
                                            />
                                            <FormControlLabel
                                                value="1"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_1")}
                                            />
                                            <FormControlLabel
                                                value="2"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_2")}
                                            />
                                        </Box>
                                    </RadioGroup>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">{t("FIELDS.CONF_TAX_TYPES_ITEMS")}</FormLabel>
                            <Controller
                                rules={{ required: true }}
                                control={control}
                                name="conf_tax_types_items"
                                render={({ field }) => (
                                    <RadioGroup {...field}>
                                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                            <FormControlLabel
                                                value="0"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_0")}
                                            />
                                            <FormControlLabel
                                                value="1"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_1")}
                                            />
                                            <FormControlLabel
                                                value="2"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_2")}
                                            />
                                        </Box>
                                    </RadioGroup>
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4} xl={3}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">{t("FIELDS.CONF_UNIT_KEYS")}</FormLabel>
                            <Controller
                                rules={{ required: true }}
                                control={control}
                                name="conf_unit_keys"
                                render={({ field }) => (
                                    <RadioGroup {...field}>
                                        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                            <FormControlLabel
                                                value="0"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_0")}
                                            />
                                            <FormControlLabel
                                                value="1"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_1")}
                                            />
                                            <FormControlLabel
                                                value="2"
                                                control={<Radio />}
                                                label={t("FIELDS.CONF_VALUE_2")}
                                            />
                                        </Box>
                                    </RadioGroup>
                                )}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    p: 3,
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}
            >
                <Box>
                    <Button color="secondary" onClick={modalCloseCallback}>
                        {t("GENERAL.CANCEL")}
                    </Button>
                    <Button
                        sx={{
                            ml: 2
                        }}
                        type="submit"
                        startIcon={
                            isSubmitting ? <CircularProgress size="1rem" /> : null
                        }
                        disabled={isSubmitting || !isValid}
                        variant="contained"
                    >
                        {t(!item ? "INVOICE-CLIENT-CONFIGS.CREATE" : 'INVOICE-CLIENT-CONFIGS.EDIT')}
                    </Button>
                </Box>
            </DialogActions>
        </form>
    )
}

export default InvoiceClientConfigForm;